import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import adjustLotQty from 'features/lots/api/adjustLotQty';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, { useState } from 'react';
import { toast } from 'sonner';

/** Adjust a lot's remaining quantity by a signed delta, with a required reason. */
export default function AdjustLotQtyModal({ lot, onClose, onSuccess = () => {} }) {
  const { user } = useUserData();
  const [submitError, setSubmitError] = useState('');

  const {
    values,
    errors,
    isSubmitting,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: { deltaQty: '', reason: '' },
    validateOnBlur: false,
    validateOnChange: false,
    validate: (validateValues) => {
      // Formik validates before onSubmit — clear the previous attempt's
      // submit error here so it can't linger next to validation errors.
      setSubmitError('');
      const validationErrors = {};
      const delta = Number(validateValues.deltaQty);
      // Whole parts only — the schema rejects fractional remainingQty.
      if (!validateValues.deltaQty || !Number.isInteger(delta) || delta === 0) {
        validationErrors.deltaQty = 'Enter a non-zero whole number (negative to remove)';
      }
      if (!validateValues.reason.trim()) validationErrors.reason = 'Reason is required';
      return validationErrors;
    },
    onSubmit: async (submitValues) => {
      try {
        await adjustLotQty({
          lotId: lot.id,
          deltaQty: Number(submitValues.deltaQty),
          reason: submitValues.reason.trim(),
          user: { uid: user.id, email: user.email },
        });
        toast.success(`Adjusted ${lot.code} by ${submitValues.deltaQty}`);
        onSuccess();
        onClose();
      } catch (error) {
        setSubmitError(error.message);
      }
    },
  });

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={`Adjust quantity — ${lot.code}`}
      footer={(
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
              Adjust
            </Button>
          </SpaceBetween>
        </Box>
      )}
    >
      <SpaceBetween direction="vertical" size="m">
        {submitError ? <Alert type="error">{submitError}</Alert> : null}
        <FormField
          label="Change"
          description={`Remaining: ${lot.remainingQty}. Positive adds, negative removes.`}
          errorText={errors.deltaQty}
        >
          <Input
            type="number"
            value={values.deltaQty}
            onChange={({ detail }) => setFieldValue('deltaQty', detail.value)}
          />
        </FormField>
        <FormField label="Reason" errorText={errors.reason}>
          <Input
            value={values.reason}
            onChange={({ detail }) => setFieldValue('reason', detail.value)}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

AdjustLotQtyModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  lot: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
